import { driverAvatarImageClass } from '@multirx/shared/game/driverAvatarCrop.js';
import { displayLapCurrent, raceLapIndicatorFromField } from '@multirx/shared/game/lapDisplay.js';
import type { RaceOvertakeBioLine } from '@multirx/shared/game/overtakeStats.js';
import { normalizeCountryCode } from '@multirx/shared/types/nationalities.js';

export { displayLapCurrent, raceLapIndicatorFromField };

export type ProfileStatusVariant = 'racing' | 'finished' | 'waiting';

export interface PlayerProfileTooltipData {
  driverName: string;
  carName: string | null;
  driverImagePath?: string | null;
  /** Division pilote (D2–D4 : cadrage tête + haut du corps sur l’avatar). */
  driverDivision?: number | null;
  /** Code pays (ex. FR) — fond drapeau de la bannière photo (pas l’avatar) */
  nationalityCode?: string;
  /** Suffixe « (vous) » après le nom (hors majuscules du nom). */
  isOwnDriver?: boolean;
  rank: number;
  /** Filigrane photo (défaut `P${rank}` ; ex. classement saison : `1er`, `2ème`). */
  rankLabel?: string;
  statusLabel: string;
  statusVariant: ProfileStatusVariant;
  handle: string;
  /** Texte libre (legacy) — préférer `bioLines` pour les dépassements. */
  bio: string | null;
  /** Dépassements : une ligne réussis (vert), une ligne contrés (rouge). */
  bioLines?: RaceOvertakeBioLine[] | null;
  /** Titres / palmarès (pastilles). */
  titles?: string[];
  stats: { value: string; label: string }[];
  /** Lignes secondaires sous les stats (ex. meilleurs tours). */
  highlights?: string[];
  footer: string | null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function driverInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

function formatDisplayName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '—';
  return escapeHtml(trimmed.toUpperCase());
}

function renderBioBlock(data: PlayerProfileTooltipData): string {
  const lines = data.bioLines?.filter((l) => l.text.trim()) ?? [];
  if (lines.length > 0) {
    return `<div class="prof-bio-stack">${lines
      .map((l) => `<div class="prof-bio prof-bio--${l.kind}">${escapeHtml(l.text)}</div>`)
      .join('')}</div>`;
  }
  if (data.bio) {
    return `<div class="prof-bio">${escapeHtml(data.bio)}</div>`;
  }
  return '';
}

/** @deprecated Préférer raceLapIndicatorFromField pour le HUD global. */
export function raceLapIndicatorFromLeader(
  _ranking: readonly { playerIndex: number }[],
  completedLaps: readonly number[],
  finished: readonly boolean[],
  numLaps: number
): { current: number; total: number } {
  return raceLapIndicatorFromField(completedLaps, finished, numLaps);
}

export function buildPlayerProfileTooltipHtml(data: PlayerProfileTooltipData): string {
  const initials = driverInitials(data.driverName);
  const rankMark = data.rankLabel?.trim() || `P${data.rank}`;
  const statusClass = `prof-status-badge prof-status-badge--${data.statusVariant}`;
  const iso = normalizeCountryCode(data.nationalityCode?.trim() ?? '');
  const photoClass = iso ? 'prof-photo prof-photo--has-flag' : 'prof-photo';
  const photoFlagBg = iso
    ? `<span class="prof-photo-flag fi fi-${escapeHtml(iso.toLowerCase())}" aria-hidden="true"></span>`
    : '';
  const statsHtml = data.stats
    .map(
      (s) =>
        `<div class="pstat"><span class="psv">${escapeHtml(s.value)}</span><span class="psl">${escapeHtml(s.label)}</span></div>`
    )
    .join('');
  const titles = data.titles?.filter((t) => t.trim()) ?? [];
  const titlesHtml =
    titles.length > 0
      ? `<div class="prof-titles">${titles
          .map((t) => `<span class="prof-title-chip">${escapeHtml(t)}</span>`)
          .join('')}</div>`
      : '';
  const highlights = data.highlights?.filter((h) => h.trim()) ?? [];
  const highlightsHtml =
    highlights.length > 0
      ? `<ul class="prof-highlights">${highlights
          .map((h) => `<li>${escapeHtml(h)}</li>`)
          .join('')}</ul>`
      : '';
  const footerHtml = data.footer ? `<div class="prof-footer">${escapeHtml(data.footer)}</div>` : '';

  const avatarImage = data.driverImagePath?.trim();
  const avatarClass = driverAvatarImageClass('prof-avatar-image', data.driverDivision, avatarImage);
  const avatarHtml = avatarImage
    ? `<img src="${escapeHtml(avatarImage)}" alt="${escapeHtml(data.driverName)}" class="${avatarClass}" />`
    : escapeHtml(initials);

  return `<div class="card-profile">
  <div class="${photoClass}">
    ${photoFlagBg}
    <div class="prof-photo-num" aria-hidden="true">${escapeHtml(rankMark)}</div>
    <div class="prof-avatar">${avatarHtml}</div>
    <div class="${statusClass}">${escapeHtml(data.statusLabel)}</div>
  </div>
  <div class="prof-body">
    <div class="prof-handle">${escapeHtml(data.handle)}</div>
    <div class="prof-name">${formatDisplayName(data.driverName)}${data.isOwnDriver ? ' (vous)' : ''}</div>
    ${titlesHtml}
    ${renderBioBlock(data)}
  </div>
  <div class="prof-stats">${statsHtml}</div>
  ${highlightsHtml}
  ${footerHtml}
</div>`;
}
